/**
 * WorkoutField Use Case Hook
 * Manages exercise array within a workout using React Hook Form
 * Requirements: 1.3, 4.5, 6.1, 6.2
 */

import {useState, useCallback} from 'react';
import {useFieldArray, UseFormReturn} from 'react-hook-form';
import type {WorkoutRoutineFormData} from '../../WorkoutRoutineForm.schema';
import {
  createDefaultExerciseWorkout,
  isPersistedItem,
} from '../../WorkoutRoutineForm.schema';
import type {UseWorkoutFieldReturn} from './WorkoutField.type';

/**
 * Hook for managing exercises within a workout
 * Implements useFieldArray for the exerciseWorkouts array
 *
 * @param form - React Hook Form instance
 * @param groupIndex - Index of the parent workout group
 * @param workoutIndex - Index of the parent workout
 * @returns Exercise field array and action handlers
 */
export function useWorkoutField(
  form: UseFormReturn<WorkoutRoutineFormData>,
  groupIndex: number,
  workoutIndex: number,
): UseWorkoutFieldReturn {
  const [isExpanded, setIsExpanded] = useState(true);

  // useFieldArray for managing exercises within this workout
  // Requirements: 1.2 - useFieldArray for managing dynamic collections
  const exercisesField = useFieldArray({
    control: form.control,
    name: `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts`,
    keyName: 'fieldId',
  });

  /**
   * Toggle expanded/collapsed state
   */
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  /**
   * Add a new exercise to this workout
   * Requirements: 4.5 - append to exerciseWorkouts array
   */
  const addExercise = useCallback(() => {
    const newExercise = createDefaultExerciseWorkout();
    exercisesField.append(newExercise);
  }, [exercisesField]);

  /**
   * Remove an exercise from this workout
   * Requirements: 4.3 - mark with _destroy if persisted, or remove if new
   *
   * @param index - Index of the exercise to remove
   */
  const removeExercise = useCallback(
    (index: number) => {
      const exercise = form.getValues(
        `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${index}`,
      );

      if (isPersistedItem(exercise)) {
        // Mark persisted items with _destroy flag
        form.setValue(
          `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${index}._destroy`,
          true,
        );
      } else {
        // Remove new items directly
        exercisesField.remove(index);
      }
    },
    [form, exercisesField, groupIndex, workoutIndex],
  );

  /**
   * Move an exercise from one position to another
   * Requirements: 4.6 - support reordering items
   *
   * @param from - Source index
   * @param to - Destination index
   */
  const moveExercise = useCallback(
    (from: number, to: number) => {
      const exercises = form.getValues(
        `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts`,
      );

      // Validate indices
      if (
        from < 0 ||
        from >= exercises.length ||
        to < 0 ||
        to >= exercises.length ||
        from === to
      ) {
        return;
      }

      exercisesField.move(from, to);
    },
    [form, exercisesField, groupIndex, workoutIndex],
  );

  return {
    exercisesField,
    isExpanded,
    toggleExpanded,
    addExercise,
    removeExercise,
    moveExercise,
  };
}

export default useWorkoutField;
